import React, { useState } from 'react';
import { BarChart, AreaChart, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации результатов WHR
 */
const ResultsVisualization = ({ results, formData }) => {
  const [showDetails, setShowDetails] = useState(false);
  
  if (!results) return null;

  // Получаем категории WHR для данного пола
  const categories = formData.gender === 'female' 
    ? [
        { id: 'extreme_low', name: 'Экстремально низкий', max: 0.65, color: '#d32f2f' },
        { id: 'low', name: 'Низкий', min: 0.65, max: 0.75, color: '#ffa726' },
        { id: 'normal', name: 'Нормальный', min: 0.75, max: 0.85, color: '#4caf50' },
        { id: 'high', name: 'Повышенный', min: 0.85, max: 0.9, color: '#ffa726' },
        { id: 'extreme_high', name: 'Высокий', min: 0.9, max: 1.0, color: '#d32f2f' }
      ]
    : [
        { id: 'extreme_low', name: 'Экстремально низкий', max: 0.85, color: '#d32f2f' },
        { id: 'low', name: 'Низкий', min: 0.85, max: 0.9, color: '#ffa726' },
        { id: 'normal', name: 'Нормальный', min: 0.9, max: 0.95, color: '#4caf50' },
        { id: 'high', name: 'Повышенный', min: 0.95, max: 1.0, color: '#ffa726' },
        { id: 'extreme_high', name: 'Высокий', min: 1.0, max: 1.1, color: '#d32f2f' }
      ];
  
  // Определяем минимальное и максимальное значение для шкалы
  const scaleMin = formData.gender === 'female' ? 0.6 : 0.8;
  const scaleMax = formData.gender === 'female' ? 1.0 : 1.1;
  
  // Расчет положения указателя в процентах
  const getPointerPosition = () => {
    const totalRange = scaleMax - scaleMin;
    const valuePosition = Math.min(Math.max(results.whr, scaleMin), scaleMax) - scaleMin;
    return (valuePosition / totalRange) * 100;
  };
  
  // Создание шкалы категорий
  const createCategoryScale = () => {
    const totalRange = scaleMax - scaleMin;
    
    return categories.map((category, index) => {
      let width = 0;
      let left = 0;
      
      if (category.min !== undefined && category.max !== undefined) {
        // Для категорий с мин и макс значениями
        width = ((category.max - category.min) / totalRange) * 100;
        left = ((category.min - scaleMin) / totalRange) * 100;
      } else if (category.max !== undefined) {
        // Для первой категории (только макс)
        width = ((category.max - scaleMin) / totalRange) * 100;
        left = 0;
      } else if (category.min !== undefined) {
        // Для последней категории (только мин)
        width = ((scaleMax - category.min) / totalRange) * 100;
        left = ((category.min - scaleMin) / totalRange) * 100;
      }
      
      return (
        <div 
          key={category.id}
          className="absolute h-full rounded-sm"
          style={{
            left: `${left}%`,
            width: `${width}%`,
            backgroundColor: category.color,
            opacity: results.category.id === category.id ? 1 : 0.3
          }}
          title={`${category.name}: ${category.min || scaleMin} - ${category.max || scaleMax}`}
        />
      );
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Визуализация результатов
        </h3>
        <button
          className="text-sm text-gray-500 dark:text-gray-400 flex items-center"
          onClick={() => setShowDetails(!showDetails)}
        >
          {showDetails ? (
            <>
              Скрыть детали 
              <ChevronUp className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              Показать детали 
              <ChevronDown className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      </div>

      {/* WHR шкала */}
      <div className="mb-8">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>Низкий</span>
          <span>Нормальный</span>
          <span>Высокий</span>
        </div>
        <div className="relative h-6 bg-gray-200 dark:bg-gray-700 rounded-md overflow-hidden">
          {createCategoryScale()}
          
          {/* Указатель текущего значения */}
          <div 
            className="absolute w-0.5 h-full bg-gray-800 dark:bg-white top-0 z-10"
            style={{ left: `${getPointerPosition()}%` }}
          >
            <div className="absolute -left-2 -top-7 bg-gray-800 dark:bg-white text-white dark:text-gray-900 px-2 py-0.5 rounded text-xs whitespace-nowrap">
              {formatNumber(results.whr, 2)}
            </div>
          </div>
        </div>
        <div className="flex justify-between text-xs text-gray-700 dark:text-gray-300 mt-1">
          <span>{formatNumber(scaleMin, 2)}</span>
          <span>{formatNumber((scaleMin + scaleMax) / 2, 2)}</span>
          <span>{formatNumber(scaleMax, 2)}</span>
        </div>
      </div>

      {/* Соотношение талии и бедер */}
      <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
        <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <Activity className="w-4 h-4 mr-1.5 text-emerald-500" />
          Соотношение талии и бедер
        </h4>
        
        <div className="relative mx-auto max-w-sm h-64 flex items-center justify-center">
          {/* Силуэт и пропорции */}
          <svg viewBox="0 0 100 250" className="w-full h-full">
            <defs>
              <linearGradient id="body-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={formData.gender === 'female' ? "#f48fb1" : "#90caf9"} />
                <stop offset="100%" stopColor={formData.gender === 'female' ? "#f06292" : "#42a5f5"} />
              </linearGradient>
            </defs>
            
            {/* Фигура */}
            {formData.gender === 'female' ? (
              // Женский силуэт с текущими пропорциями
              <path 
                d={`M50,20 
                  C65,20 65,50 65,60 
                  C65,70 ${55 + results.whr * 20},90 ${55 + results.whr * 20},100 
                  C${55 + results.whr * 20},120 50,140 50,160 
                  C50,180 65,200 65,220 
                  C65,240 60,250 50,250 
                  C40,250 35,240 35,220 
                  C35,200 50,180 50,160 
                  C50,140 ${45 - results.whr * 20},120 ${45 - results.whr * 20},100 
                  C${45 - results.whr * 20},90 35,70 35,60 
                  C35,50 35,20 50,20`}
                fill="url(#body-gradient)"
                stroke="#e91e63"
                strokeWidth="0.5"
                opacity="0.8"
              />
            ) : (
              // Мужской силуэт с текущими пропорциями
              <path 
                d={`M50,20 
                  C65,20 65,40 65,60 
                  C65,70 ${60 + results.whr * 15},90 ${60 + results.whr * 15},100 
                  C${60 + results.whr * 15},120 55,140 55,160 
                  C55,180 60,200 60,220 
                  C60,240 55,250 50,250 
                  C45,250 40,240 40,220 
                  C40,200 45,180 45,160 
                  C45,140 ${40 - results.whr * 15},120 ${40 - results.whr * 15},100 
                  C${40 - results.whr * 15},90 35,70 35,60 
                  C35,40 35,20 50,20`}
                fill="url(#body-gradient)"
                stroke="#2196f3"
                strokeWidth="0.5"
                opacity="0.8"
              />
            )}
            
            {/* Линии для талии и бедер */}
            <line 
              x1="30" 
              y1="100" 
              x2="70" 
              y2="100" 
              stroke="#e91e63" 
              strokeWidth="1" 
              strokeDasharray="2"
            />
            <text 
              x="75" 
              y="103" 
              fill={formData.gender === 'female' ? "#e91e63" : "#2196f3"} 
              fontSize="6"
              fontWeight="bold"
            >
              Талия
            </text>
            
            <line 
              x1="30" 
              y1="160" 
              x2="70" 
              y2="160" 
              stroke="#9c27b0" 
              strokeWidth="1"
              strokeDasharray="2"
            />
            <text 
              x="75" 
              y="163" 
              fill={formData.gender === 'female' ? "#e91e63" : "#2196f3"} 
              fontSize="6"
              fontWeight="bold"
            >
              Бедра
            </text>
            
            {/* Индикаторы значений */}
            <text 
              x="50" 
              y="12" 
              fill="#333333"
              fontSize="8"
              fontWeight="bold"
              textAnchor="middle"
            >
              WHR = {formatNumber(results.whr, 2)}
            </text>
          </svg>
        </div>
      </div>

      {showDetails && (
        <>
          {/* Сравнение с нормой */}
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
              Сравнение с нормативными значениями
            </h4>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Показатель
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ваше значение
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Рекомендуемое
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Отклонение
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      WHR
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatNumber(results.whr, 2)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {results.idealMeasurements.whr}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={
                        results.whr > parseFloat(results.idealMeasurements.whr) * 1.1
                          ? "text-red-600 dark:text-red-400"
                          : results.whr < parseFloat(results.idealMeasurements.whr) * 0.9
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-emerald-600 dark:text-emerald-400"
                      }>
                        {(((results.whr / parseFloat(results.idealMeasurements.whr)) - 1) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      Обхват талии
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {results.measurements.waist} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {results.idealMeasurements.waist} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={
                        results.measurements.waist > parseFloat(results.idealMeasurements.waist) * 1.1
                          ? "text-red-600 dark:text-red-400"
                          : results.measurements.waist < parseFloat(results.idealMeasurements.waist) * 0.9
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-emerald-600 dark:text-emerald-400"
                      }>
                        {(((results.measurements.waist / parseFloat(results.idealMeasurements.waist)) - 1) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      Обхват бедер
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {results.measurements.hip} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {results.idealMeasurements.hip} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={
                        results.measurements.hip > parseFloat(results.idealMeasurements.hip) * 1.1
                          ? "text-amber-600 dark:text-amber-400"
                          : results.measurements.hip < parseFloat(results.idealMeasurements.hip) * 0.9
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-emerald-600 dark:text-emerald-400"
                      }>
                        {(((results.measurements.hip / parseFloat(results.idealMeasurements.hip)) - 1) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      Индекс массы тела
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatNumber(results.bmi, 1)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      18.5 - 24.9
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={
                        results.bmi > 25
                          ? "text-red-600 dark:text-red-400"
                          : results.bmi < 18.5
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-emerald-600 dark:text-emerald-400"
                      }>
                        {results.bmi > 25
                          ? `+${(results.bmi - 25).toFixed(1)}`
                          : results.bmi < 18.5
                            ? `-${(18.5 - results.bmi).toFixed(1)}`
                            : "В норме"
                        }
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      Соотношение талии к росту
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {formatNumber(results.measurements.waistToHeightRatio, 2)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      &lt; 0.5
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span className={
                        results.measurements.waistToHeightRatio > 0.5
                          ? "text-red-600 dark:text-red-400"
                          : "text-emerald-600 dark:text-emerald-400"
                      }>
                        {results.measurements.waistToHeightRatio > 0.5
                          ? `+${((results.measurements.waistToHeightRatio - 0.5) * 100).toFixed(1)}%`
                          : "В норме"
                        }
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Интерпретация */}
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-2">
            <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
              Интерпретация WHR (соотношения талия-бедра)
            </h4>
            
            <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <p>
                <strong>Что такое WHR:</strong> Соотношение талии к бедрам (WHR) — это показатель распределения жира в организме, 
                который рассчитывается путем деления окружности талии на окружность бедер.
              </p>
              
              <p>
                <strong>Почему это важно:</strong> WHR является значимым предиктором риска сердечно-сосудистых заболеваний, 
                диабета и общей смертности. Люди с высоким WHR (с отложениями жира в области живота, "фигура яблока") 
                имеют более высокий риск по сравнению с людьми с более низким WHR (с отложениями жира на бедрах и ягодицах, "фигура груши").
              </p>
              
              <p>
                <strong>Нормы WHR в России:</strong> Согласно рекомендациям российских медицинских организаций, нормальные значения WHR:
              </p>
              <ul className="list-disc list-inside pl-4 space-y-1">
                <li>Для женщин: 0.75-0.85</li>
                <li>Для мужчин: 0.9-0.95</li>
              </ul>
              
              <p>
                <strong>Дополнительные показатели:</strong> Кроме WHR, полезно отслеживать окружность талии и соотношение талии к росту. 
                Согласно рекомендациям, окружность талии не должна превышать половину роста (соотношение талии к росту &lt; 0.5).
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ResultsVisualization;